/**
 * ScheduleFilterDrawer — dynamic filter editor for scheduled reports.
 *
 * The accordion sections and the Response Time control follow the report type, and
 * come from scheduleFilterConfig (utils/reportTypes.js) — the same
 * presets the live report pages use, so the editor offers exactly what the report's
 * own drawer does (docs/DUPLICATION_AUDIT.md, B4):
 *   - Alert family (1, 4, 5, 15, 16): all five groups + Response Time
 *   - Maintenance (10): Event Types
 *   - Wellness (2, 3, 9, 11, 13): Areas + Residents + Device Categories
 *   - Environmental (6, 7, 8, 12): Areas + Residents + Reasons
 *   - Call Logs (14): Call Types
 * A key a type does not show is still read from and written back to the stored
 * filter terms, so hiding a group never drops a saved value.
 *
 * No Time Range accordion — the schedule interval is set separately in ScheduleDrawer.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import FilterSubSection from '../../../../components/v2/FilterSubSection.jsx';
import { selectionFromStoredReasonIds } from '../../../../utils/reasonFilterIds.js';
import { filterTermsToState, stateToFilterTerms } from '../../../../utils/reportFilterTerms.js';
import { scheduleFilterConfig } from '../../../../utils/reportTypes.js';
import Drawer from '../../../../components/v2/Drawer.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------




/** parseFilterTerms plus the reason mapping: stored reason ids are the expanded
 *  cloud + local set, and the checkboxes are keyed by cloud id. */
function loadWorking(filterTerms, filterOptions) {
    /* The shared parser returns arrays; this drawer works in Sets, and always has a
       responseTime (the shared parser only returns one when it was stored). */
    const parsed = filterTermsToState(filterTerms);
    const w = {};
    Object.entries(parsed).forEach(([k, v]) => { w[k] = k === 'responseTime' ? v : new Set(v); });
    if (!w.responseTime) w.responseTime = { enabled: false, operator: 'lt', value: 60 };
    if (w.reasons && w.reasons.size > 0) {
        w.reasons = new Set(selectionFromStoredReasonIds([...w.reasons], filterOptions?.reasonCodes));
    }
    return w;
}

function optionsFor(subDef, filterOptions) {
    const options = filterOptions?.[subDef.optionsKey] || [];
    return subDef.excludeNoReason
        ? options.filter((o) => !(String(o.cloud_reason_code_id) === '0' && String(o.reason_code_id) === '0'))
        : options;
}

function cloneWorking(w) {
    const result = {};
    Object.keys(w).forEach((k) => {
        if (k === 'responseTime') {
            result[k] = { ...w[k] };
        } else {
            result[k] = new Set(w[k]);
        }
    });
    return result;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ScheduleFilterDrawer = ({
    open, onClose, onApply,
    reportTypeId, filterOptions, currentFilterTerms,
}) => {
    const { sections, responseTime: showResponseTime } = useMemo(() => scheduleFilterConfig(reportTypeId), [reportTypeId]);

    const [working, setWorking] = useState(() => loadWorking(currentFilterTerms, filterOptions));
    const [expandedSections, setExpandedSections] = useState(new Set());

    useEffect(() => {
        if (open) setWorking(loadWorking(currentFilterTerms, filterOptions));
    }, [open, currentFilterTerms]);

    const toggleSection = useCallback((id) => {
        setExpandedSections((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
    }, []);

    const handleToggle = useCallback((key, id) => {
        setWorking((prev) => { const n = cloneWorking(prev); if (!n[key]) n[key] = new Set(); if (n[key].has(id)) n[key].delete(id); else n[key].add(id); return n; });
    }, []);

    const handleSelectAll = useCallback((key, allIds) => {
        setWorking((prev) => { const n = cloneWorking(prev); n[key] = new Set(allIds); return n; });
    }, []);

    const handleClear = useCallback((key) => {
        setWorking((prev) => { const n = cloneWorking(prev); n[key] = new Set(); return n; });
    }, []);

    const handleRespToggle = useCallback((e) => {
        setWorking((prev) => { const n = cloneWorking(prev); n.responseTime = { ...n.responseTime, enabled: e.target.checked }; return n; });
    }, []);

    const handleRespOperator = useCallback((e) => {
        setWorking((prev) => { const n = cloneWorking(prev); n.responseTime = { ...n.responseTime, operator: e.target.value }; return n; });
    }, []);

    const handleRespValue = useCallback((e) => {
        setWorking((prev) => { const n = cloneWorking(prev); n.responseTime = { ...n.responseTime, value: parseInt(e.target.value, 10) || 0 }; return n; });
    }, []);

    const getSectionCount = useCallback((section) => {
        let count = 0;
        section.subs.forEach((sub) => { count += (working[sub.key]?.size || 0); });
        if (section.id === 'events' && showResponseTime && working.responseTime?.enabled) count += 1;
        return count;
    }, [working, showResponseTime]);

    const handleApply = useCallback(() => {
        const serialized = stateToFilterTerms(working, filterOptions);
        onApply(serialized);
    }, [working, filterOptions, onApply]);

    const handleClearAll = useCallback(() => {
        const empty = {};
        sections.forEach((s) => s.subs.forEach((sub) => { empty[sub.key] = new Set(); }));
        empty.responseTime = { enabled: false, operator: 'lt', value: 60 };
        setWorking(empty);
    }, [sections]);

    return (
        <>
            <Drawer
                open={open}
                onClose={onClose}
                title="Edit Report Filters"
                size="md"
                bodyProps={{ style: { overflowY: 'auto', flex: 1 } }}
                footer={(
                    <>
                        <button className="dp-btn dp-btn--cancel" onClick={handleClearAll} type="button">Clear All</button>
                        <button className="dp-btn dp-btn--primary" onClick={handleApply} type="button">Apply Filters</button>
                    </>
                )}
            >
                {sections.map((section) => {
                    const isExpanded = expandedSections.has(section.id);
                    const count = getSectionCount(section);
                    return (
                        <div key={section.id} className="ar-accordion">
                            <button className={`ar-accordion__header${isExpanded ? ' ar-accordion__header--open' : ''}`} onClick={() => toggleSection(section.id)} type="button">
                                <span className="ar-accordion__icon">{isExpanded ? '\u25BC' : '\u25B6'}</span>
                                <span className="ar-accordion__label">{section.label}</span>
                                {count > 0 ? (<span className="ar-accordion__count">{count} selected</span>) : null}
                            </button>
                            {isExpanded ? (
                                <div className="ar-accordion__body">
                                    {section.id === 'events' && showResponseTime ? (
                                        <div className="ar-filter-section">
                                            <div className="ar-filter-section__header">
                                                <span className="ar-filter-section__label">Response Time</span>
                                            </div>
                                            <div className="ar-resp-filter">
                                                <label className="ar-resp-filter__toggle">
                                                    <input type="checkbox" checked={working.responseTime?.enabled || false} onChange={handleRespToggle} />
                                                    <span>Enable</span>
                                                </label>
                                                {working.responseTime?.enabled ? (
                                                    <div className="ar-resp-filter__controls">
                                                        <select className="ar-resp-filter__select" value={working.responseTime?.operator || 'lt'} onChange={handleRespOperator}>
                                                            <option value="lt">Less than</option>
                                                            <option value="gt">Greater than</option>
                                                            <option value="eq">Equal to</option>
                                                        </select>
                                                        <input className="ar-resp-filter__input" type="number" value={working.responseTime?.value || 60} onChange={handleRespValue} min={0} />
                                                        <span className="ar-resp-filter__unit">min</span>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    ) : null}
                                    {section.subs.map((sub) => (
                                        <FilterSubSection key={sub.key} subDef={sub} options={optionsFor(sub, filterOptions)} selected={working[sub.key] || new Set()} onToggle={handleToggle} onSelectAll={handleSelectAll} onClear={handleClear} />
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </Drawer>
        </>
    );
};

export default ScheduleFilterDrawer;
